import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, Database, Clock, BarChart, Phone, Wallet } from 'lucide-react';
import Header from '../components/Header';
import UsersTab from '../components/admin/UsersTab';
import LogsTab from '../components/admin/LogsTab';
import LineItemsTab from '../components/admin/LineItemsTab';
import AccountsTab from '../components/admin/AccountsTab';
import StatisticsTab from '../components/admin/StatisticsTab';
import ContactsTab from '../components/admin/ContactsTab';
import WithdrawalsTab from '../components/admin/WithdrawalsTab';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'admin' && user.role !== 'supervisor') {
      navigate('/');
      return;
    }

    setLoading(false);
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex space-x-4 mb-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'users'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Users className="h-5 w-5 mr-2" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'logs'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Clock className="h-5 w-5 mr-2" />
              System Logs
            </button>
            <button
              onClick={() => setActiveTab('lines')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'lines'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FileText className="h-5 w-5 mr-2" />
              Line Items
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'accounts'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Database className="h-5 w-5 mr-2" />
              Accounts
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'statistics'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <BarChart className="h-5 w-5 mr-2" />
              Statistics
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'contacts'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Phone className="h-5 w-5 mr-2" />
              Contacts
            </button>
            <button
              onClick={() => setActiveTab('withdrawals')}
              className={`flex items-center px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === 'withdrawals'
                  ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Wallet className="h-5 w-5 mr-2" />
              Withdrawals
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            {activeTab === 'users' && <UsersTab />}
            {activeTab === 'logs' && <LogsTab />}
            {activeTab === 'lines' && <LineItemsTab />}
            {activeTab === 'accounts' && <AccountsTab />}
            {activeTab === 'statistics' && <StatisticsTab />}
            {activeTab === 'contacts' && <ContactsTab />}
            {activeTab === 'withdrawals' && <WithdrawalsTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;